/*
 * Mapping functions for the serialized output from serde.
 *
 * wasm_bindgen can't handle structs / vec, so serialization was the best way, but
 * serialized output can't do enums as well as wasm_bindgen, hence the below string mappings.
 */

import {
  ChordInScale,
  ChordType,
  Interval,
  MusicNoteIdentity,
  MusicNoteName,
  MusicNoteOccurrence,
  MusicNoteWithinChord,
  MusicNoteWithinScale,
  MusicScale,
  NoteAccidental,
  NoteScaleOverride,
  NoteSettings,
  Octave,
} from "../../wasm/pkg/wasm";
import { buildChordTypeFromSortedChordIntervals } from "../functions/buildChordTypeFromSortedChordIntervals";
import { ChordHistoryLabelMode } from "./ChordHistoryOptions.model";

export type StreamFormat = "WEBM_PCM" | "RAW";

export function listAllNoteIds(): Array<MusicNoteIdentity> {
  return ["A", "AsharpBflat", "B", "C", "CsharpDflat", "D", "DsharpEflat", "E", "F", "FsharpGflat", "G", "GsharpAflat"];
}

export function toNoteAndOctaveName(note_id: MusicNoteIdentity, octave: Octave, scale: MusicScale | undefined): string {
  return toNoteName(note_id, scale) + toOctaveName(octave);
}

export function toNoteName(note_id: MusicNoteIdentity, scale?: MusicScale | undefined) {
  const noteInFamily = scale?.family.notes.find((n) => n.note_id === note_id);
  if (noteInFamily) {
    return printNoteName(noteInFamily.note_name);
  } else {
    return toNoteNameNoContext(note_id, scale?.family.scale_uses_sharps_or_flats);
  }
}
export function printNoteName(note_name: MusicNoteName) {
  return note_name.replace("flat", "b").replace("sharp", "#");
}

export function toNoteNameNoContext(note: MusicNoteIdentity, sharpOrFlat?: NoteAccidental): string {
  if (note.length === 1) {
    return note;
  }
  let [sharpName, flatName] = note.split("sharp");
  if (sharpOrFlat === "Flat") {
    return flatName.replace("flat", "b");
  } else {
    return sharpName + "#";
  }
}

function getMajorRootNote(scale?: MusicScale): MusicNoteWithinScale | undefined {
  return scale?.shape_notes.find((n) => n.minor_scale_degree === "MinorThird") || undefined; // every minor scale type includes the major tonic
}

function getMinorRootNote(scale?: MusicScale): MusicNoteWithinScale | undefined {
  return scale?.shape_notes[0] || undefined;
}

export function printScaleRootLetter(scale: MusicScale | undefined, isPreferMajor: boolean | undefined): string {
  const note = isPreferMajor ? getMajorRootNote(scale) : getMinorRootNote(scale); // scale?.shape_notes[0].note_name || "";
  return note ? printNoteName(note.note_name) : "";
}

export function printScaleShapeType(scale: MusicScale | undefined, isPreferMajor: boolean | undefined) {
  if (!scale?.shape) {
    return "";
  }
  switch (scale.shape) {
    case "HarmonicMinor":
      /**
       * Awkward becaues there is no equivalent.  But it works out because assuming it's a major scale
       * also results in harmonic/melodic notes being marked as accidentals or silenced more aggressively.
       */
      return isPreferMajor ? "Major" : "Harmonic Minor";
    case "MelodicMinor":
      return isPreferMajor ? "Major" : "Melodic Minor";
    case "Pentatonic":
      return isPreferMajor ? "Major Pentatonic" : "Minor Pentatonic";
    case "Blues":
      return isPreferMajor ? "Major Blues" : "Minor Blues";
    case "Minor":
      return isPreferMajor ? "Major" : "Minor";
    default:
      return "unsupported scale shape";
  }
}

export function toOctaveName(octave: Octave): string {
  const num = toOctaveNumber(octave);
  if (Number.isNaN(num)) {
    return "BUG!";
  }
  return `${num}`;
}

export function toOctaveNumber(octave: Octave | undefined): number {
  switch (octave) {
    case "Two":
      return 2;
    case "Three":
      return 3;
    case "Four":
      return 4;
    case "Five":
      return 5;
    case "Six":
      return 6;
    case "Seven":
      return 7;
    default:
      return NaN;
  }
}
export type ChordNameOptions = {
  labelMode?: ChordHistoryLabelMode;
  isScaleMajor?: boolean;
};
export type ChordInScaleEdit = Omit<ChordInScale, "chord_type"> & { chord_type: ChordType | undefined }; // user edit could invalidate chord
export function toShortChordName(chord: ChordInScaleEdit, opts: ChordNameOptions = {}): string {
  const rootName =
    opts.labelMode === ChordHistoryLabelMode.RootDegree
      ? toRootDegreeFormat(chord.root_note.interval.scale_degree, opts.isScaleMajor)
      : chord.root_note.note_name.replace("sharp", "#").replace("flat", "b");
  let shortChordType = toShortChordType(chord.chord_type);
  return `${rootName}${shortChordType}`;
}
export function toShortChordNameOverBass(chord: ChordInScaleEdit, bass_note?: MusicNoteWithinChord) {
  if (!bass_note) {
    // chord.bass_note_id
    bass_note = chord.notes.find((n) => n.note_id === chord.bass_note_id);
  }
  const shortName = toShortChordName(chord);
  const moreName = !bass_note || bass_note.note_id === chord.root_note.note_id ? "" : ` / ${printNoteName(bass_note.note_name)}`;
  return `${shortName}${moreName}`;
}

export function buildNoteNameMap(scale?: MusicScale): Record<MusicNoteIdentity, string> {
  const map = {} as Record<MusicNoteIdentity, string>;
  if (scale) {
    scale.shape_notes.forEach((note) => {
      // If we're in the context of a scale, other notes are not printed (names not required)
      map[note.note_id] = printNoteName(note.note_name);
    });
  } else {
    listAllNoteIds().forEach((nid) => {
      map[nid] = toNoteNameNoContext(nid);
    });
  }
  return map;
}

const ROM_NUM = {
  I: "\u{2160}",
  II: "\u{2161}",
  III: "\u{2162}",
  IV: "\u{2163}",
  V: "\u{2164}",
  VI: "\u{2165}",
  VII: "\u{2166}",
};

/**
 * Anything lowercase or #'d that comes out of this is nonsensical.
 * Perhaps forbid accidentals from being chord roots.
 */
function toRootDegreeFormat(scaleDegree: Interval, isScaleMajor?: boolean) {
  switch (scaleDegree) {
    case "Tonic":
      return isScaleMajor ? ROM_NUM.VI : ROM_NUM.I; // A
    case "MinorSecond":
      return isScaleMajor ? "vii" : "ii"; // A# - never in scale
    case "MajorSecond":
      return isScaleMajor ? ROM_NUM.VII : ROM_NUM.II; // B
    case "MinorThird":
      return isScaleMajor ? ROM_NUM.I : ROM_NUM.III; // C
    case "MajorThird":
      return isScaleMajor ? "ii" : "III#"; // C# -- never in scale
    case "PerfectFourth":
      return isScaleMajor ? ROM_NUM.II : ROM_NUM.IV; // D
    case "FlatFifth":
      return isScaleMajor ? "iii" : "v"; // D# // -- only in blues, unlikely root
    case "PerfectFifth":
      return isScaleMajor ? ROM_NUM.III : ROM_NUM.V; // E
    case "MinorSixth":
      return isScaleMajor ? ROM_NUM.IV : ROM_NUM.VI; // F
    case "MajorSixth":
      return isScaleMajor ? "v" : "VI#"; // F# // only melodic minor, unlikely root
    case "MinorSeventh":
      return isScaleMajor ? ROM_NUM.V : ROM_NUM.VII; // G
    case "MajorSeventh":
      return isScaleMajor ? "vi" : "VII#"; // G# // only harmonic/mel minor, unlikely root
    default:
      return "X"; // bug
  }
}

export function toShortChordType(chordType: ChordType | undefined): string {
  switch (chordType) {
    case "Five":
      return "5";
    case "MajorTriad":
      return "maj";
    case "MinorTriad":
      return "m";
    case "TriadSus2":
      return "sus2";
    case "TriadSus4":
      return "sus4";
    case "DiminishedTriad":
      return "dim";
    case "MajorSixth":
      return "6";
    case "MinorSixth":
      return "m6";
    case "SixthSus2":
      return "6sus2";
    case "SixthSus4":
      return "6sus4";
    case "MinorSeventh":
      return "m7";
    case "MinorMajorSeventh":
      return "m(maj7)";
    case "DominantSeventh":
      return "7";
    case "MajorSeventh":
      return "maj7";
    case "HalfDiminishedSeventh":
      return "m7b5";
    case "DiminishedSeventh":
      return "dim7";
    case "MinorSeventhSus2":
      return "m7sus2";
    case "MinorSeventhSus4":
      return "m7sus4";
    case "MajorSeventhSus2":
      return "7sus2";
    case "MajorSeventhSus4":
      return "7sus4";
    case "MajorNinth":
      return "maj9";
    case "MinorNinth":
      return "m9";
    case "DominantNinth":
      return "9";
    case "MajorEleventh":
      return "maj11";
    case "MinorEleventh":
      return "m11";
    case "DominantEleventh":
      return "11";
    case "AlteredFlatNinth":
      return "7,b9";
    case "MajorThirteenth":
      return "maj13";
    case "MinorThirteenth":
      return "m13";
    case "DominantMajThirteenth":
      return "13";
    case "DominantMinThirteenth":
      return "13";
    default:
      return "**"; // the user is allowed to make it not-a-valid-chord.  Should never happen without user edit.
  }
}

export function sortNotesLowestLast(arr: Array<MusicNoteOccurrence>): Array<MusicNoteOccurrence> {
  return arr.toSorted((a, b) => toNoteAndOctaveSortValue(b) - toNoteAndOctaveSortValue(a));
}
function toNoteAndOctaveSortValue(mno: MusicNoteOccurrence): number {
  const hundreds = 100 * toOctaveNumber(mno.primary_octave);
  const ones = toNoteSortValueWithinOctave(mno.identity);
  return hundreds + ones;
}

function toNoteSortValueWithinOctave(nid: MusicNoteIdentity): number {
  switch (nid) {
    case "C":
      return 0;
    case "CsharpDflat":
      return 1;
    case "D":
      return 2;
    case "DsharpEflat":
      return 3;
    case "E":
      return 4;
    case "F":
      return 5;
    case "FsharpGflat":
      return 6;
    case "G":
      return 7;
    case "GsharpAflat":
      return 8;
    case "A":
      return 9;
    case "AsharpBflat":
      return 10;
    case "B":
      return 11;
    default:
      return NaN;
  }
}

/** Frontend maintains additional info re: the contents of WASM UserSettings.notes */
export type NoteSettingsDecision = {
  settings: NoteSettings;
  why: Array<string>;
  primaryScale?: MusicScale;
};

export function updateNoteSettingsWithManualOverride(
  old: Array<NoteSettingsDecision>,
  note_id: MusicNoteIdentity,
  scale_override: NoteScaleOverride
): Array<NoteSettingsDecision> {
  const updated = old.filter((o) => o.settings.note_id !== note_id);
  if (scale_override !== "None") {
    updated.push({ settings: { note_id, scale_override }, why: ["manual"] });
  }
  return updated;
}

// gross but probably net less gross than looping array traversal
function getNextNeighborAscending(note_id: MusicNoteIdentity): MusicNoteIdentity {
  switch (note_id) {
    case "A":
      return "AsharpBflat";
    case "AsharpBflat":
      return "B";
    case "B":
      return "C";
    case "C":
      return "CsharpDflat";
    case "CsharpDflat":
      return "D";
    case "D":
      return "DsharpEflat";
    case "DsharpEflat":
      return "E";
    case "E":
      return "F";
    case "F":
      return "FsharpGflat";
    case "FsharpGflat":
      return "G";
    case "G":
      return "GsharpAflat";
    case "GsharpAflat":
      return "A";
  }
}
function getNextNeighborDescending(note_id: MusicNoteIdentity): MusicNoteIdentity {
  switch (note_id) {
    case "AsharpBflat":
      return "A";
    case "B":
      return "AsharpBflat";
    case "C":
      return "B";
    case "CsharpDflat":
      return "C";
    case "D":
      return "CsharpDflat";
    case "DsharpEflat":
      return "D";
    case "E":
      return "DsharpEflat";
    case "F":
      return "E";
    case "FsharpGflat":
      return "F";
    case "G":
      return "FsharpGflat";
    case "GsharpAflat":
      return "G";
    case "A":
      return "GsharpAflat";
  }
}

/** e.g. list the notes on a guitar string */
export function buildNotesAscendingFromNoteId(stringNote: MusicNoteIdentity, length: number): Array<MusicNoteIdentity> {
  const nidPerString = Array(length);
  nidPerString[0] = stringNote;
  for (let i = 1; i < length; i++) {
    nidPerString[i] = getNextNeighborAscending(nidPerString[i - 1]);
  }
  return nidPerString;
}

export function findNoteByHalfsteps(note_id: MusicNoteIdentity, halfSteps: number): MusicNoteIdentity {
  let target = note_id;
  const stepFunc = halfSteps > 0 ? getNextNeighborAscending : getNextNeighborDescending;
  for (let i = 0; i < Math.abs(halfSteps); i++) {
    target = stepFunc(target);
  }
  return target;
}
export function toIntervalHalfSteps(interval: Interval): number {
  switch (interval) {
    case "Tonic":
      return 0;
    case "MinorSecond":
      return 1;
    case "MajorSecond":
      return 2;
    case "MinorThird":
      return 3;
    case "MajorThird":
      return 4;
    case "PerfectFourth":
      return 5;
    case "FlatFifth":
      return 6;
    case "PerfectFifth":
      return 7;
    case "MinorSixth":
      return 8;
    case "MajorSixth":
      return 9;
    case "MinorSeventh":
      return 10;
    case "MajorSeventh":
      return 11;
    case "Octave":
      return 12;
    case "MinorNinth":
      return 13;
    case "MajorNinth":
      return 14;
    case "MinorTenth":
      return 15;
    case "MajorTenth":
      return 16;
    case "PerfectEleventh":
      return 17;
    case "AugmentedEleventh":
      return 18;
    case "PerfectTwelfth":
      return 19;
    case "MinorThirteenth":
      return 20;
    case "MajorThirteenth":
      return 21;
    case "MinorFourteenth":
      return 22;
    case "MajorFourteenth":
      return 23;
    case "PerfectFifteenth":
      return 24;
  }
}
